import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowCounterClockwiseIcon,
  ChatCircleTextIcon,
  PulseIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react';
import { fetchPlaybook, fetchSettings, patchPlaybook, patchSettings } from '@/features/account/api';
import {
  deleteAutonomyRule,
  fetchAutonomyState,
  setAutonomyPreset,
  upsertAutonomyRule,
  type AutonomyIntentView,
  type AutonomyMode,
  type AutonomyPreset,
  type AutonomyToolView,
} from '@/features/autonomy/api';
import {
  AREA_LABEL_RU,
  AREA_ORDER,
  INTENT_AREA,
  TOOL_AREA,
  type AutonomyArea,
} from '@smena/core/labels';
import { HARD_STOP_INTENTS } from '@smena/core/hard-stop';
import { previewPaymentLink } from '@smena/core/payment-link';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, PageHeader, Segmented, Switch, cn, type SegmentedOption } from '@smena/ui';

export function AgentPage() {
  const qc = useQueryClient();
  const playbookQ = useQuery({ queryKey: ['playbook'], queryFn: fetchPlaybook });
  const settingsQ = useQuery({ queryKey: ['settings'], queryFn: fetchSettings });

  const [pb, setPb] = useState<Record<string, unknown>>({});
  const [pbDirty, setPbDirty] = useState(false);
  useEffect(() => {
    if (playbookQ.data) setPb(playbookQ.data);
  }, [playbookQ.data]);

  const savePlaybook = useMutation({
    mutationFn: () => patchPlaybook(pb),
    onSuccess: () => {
      setPbDirty(false);
      void qc.invalidateQueries({ queryKey: ['playbook'] });
      toast.success('Правила сохранены');
    },
  });

  const saveSettings = useMutation({
    mutationFn: (data: Record<string, boolean>) => patchSettings(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Настройка сохранена');
    },
  });

  function setField(key: string, value: unknown) {
    setPb((cur) => ({ ...cur, [key]: value }));
    setPbDirty(true);
  }

  const s = settingsQ.data ?? {};

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={<SlidersHorizontalIcon weight="bold" />}
        title="Агент"
        description="Что сейчас работает, насколько Smena самостоятельна и по каким правилам считает цены."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PulseIcon weight="bold" className="size-4 text-green" /> Что сейчас работает
          </CardTitle>
          <p className="text-xs text-muted">
            Тумблер ставит функцию на паузу целиком - по всем объектам и гостям. Диалоги при этом
            никуда не пропадают, их видно в разделе «Внимание».
          </p>
        </CardHeader>
        {/* Переключатели показывают «работает / на паузе», а не «включить стоп-кран»:
            двойное отрицание («поставить галочку, чтобы выключить») читается неверно. */}
        <CardContent className="flex flex-col">
          <SwitchRow
            label="Ответы своими словами"
            hint="Smena формулирует ответ под вопрос гостя. На паузе - отвечает только заранее заготовленными фразами."
            on={!s.llm_off}
            onChange={(on) => saveSettings.mutate({ llm_off: !on })}
          />
          <SwitchRow
            label="Отправка ответов гостю"
            hint="На паузе Smena продолжает готовить ответы, но отправляете их вы - из раздела «Внимание»."
            on={!s.autoreply_off}
            onChange={(on) => saveSettings.mutate({ autoreply_off: !on })}
          />
          <SwitchRow
            label="Сообщения по расписанию"
            hint="Приветствие после брони и инструкция заселения за 3 часа до заезда."
            on={!s.control_off}
            onChange={(on) => saveSettings.mutate({ control_off: !on })}
          />
          <SwitchRow
            label="Задачи на уборку"
            hint="После выезда гостя Smena сама ставит уборку в расписание клинеров."
            on={!s.cleaners_off}
            onChange={(on) => saveSettings.mutate({ cleaners_off: !on })}
          />
        </CardContent>
      </Card>

      <AutonomyCard defaultMode={s.autoreply_off ? 'confirm' : 'auto'} />

      <Card>
        <CardHeader>
          <CardTitle>Правила ответов</CardTitle>
          <p className="max-w-prose text-xs leading-snug text-muted">
            По этим правилам Smena отвечает гостю про заезд, выезд и деньги. Правила общие для
            всего аккаунта.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="col-span-full text-[11px] font-bold uppercase tracking-wide text-muted">
              Ранний заезд и поздний выезд
            </div>
            <PlaybookField
              label="Ранний заезд с"
              k="earlyFrom"
              v={pb.earlyFrom as string | undefined}
              onChange={setField}
              hint="время, например 10:00"
            />
            <PlaybookField
              label="Сколько берём за ранний заезд, ₽"
              k="earlyPrice"
              v={pb.earlyPrice as number | undefined}
              onChange={setField}
              type="number"
            />
            <PlaybookField
              label="Поздний выезд до"
              k="lateUntil"
              v={pb.lateUntil as string | undefined}
              onChange={setField}
              hint="время, например 14:00"
            />
            <PlaybookField
              label="Сколько берём за поздний выезд, ₽"
              k="latePrice"
              v={pb.latePrice as number | undefined}
              onChange={setField}
              type="number"
            />
          </section>

          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="col-span-full">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted">Цена ночи</div>
              {/* Честно про ограничение: цена пока одна на аккаунт, потому что конвейер берёт
                  первый объект. Пока это так - не притворяемся, что цена «по объекту». */}
              <p className="mt-1 max-w-prose text-xs leading-snug text-muted">
                Пока цена одна на все квартиры - от неё Smena считает продление. Цены по каждому
                объекту отдельно сделаем в карточке квартиры.
              </p>
            </div>
            <PlaybookField
              label="Цена ночи, ₽"
              k="nightPrice"
              v={pb.nightPrice as number | undefined}
              onChange={setField}
              type="number"
            />
            <PlaybookField
              label="Скидка за продление, %"
              k="extendDiscountPct"
              v={pb.extendDiscountPct as number | undefined}
              onChange={setField}
              type="number"
            />
            <PlaybookField
              label="Максимальная скидка без вас, %"
              k="maxDiscountPct"
              v={pb.maxDiscountPct as number | undefined}
              onChange={setField}
              type="number"
            />
          </section>

          <section className="flex flex-col gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted">Оплата</div>
            <PaymentLinkField
              value={String(pb.paymentUrl ?? '')}
              onChange={(v) => setField('paymentUrl', v)}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted">
              Как Smena разговаривает
            </div>
            <ToneField value={String(pb.tone ?? '')} onChange={(v) => setField('tone', v)} />
            <div className="flex flex-col gap-1">
              <Label>Что отвечать гостю, когда вопрос передан вам</Label>
              <textarea
                value={String(pb.holder ?? '')}
                onChange={(e) => setField('holder', e.target.value)}
                rows={2}
                placeholder="Секундочку - уточню у коллеги и вернусь с ответом"
                className="w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
              />
              <span className="text-xs text-muted">
                Эту фразу гость видит сразу, пока вы готовите ответ.
              </span>
            </div>
          </section>

          <div className="col-span-full">
            <Button onClick={() => savePlaybook.mutate()} disabled={!pbDirty || savePlaybook.isPending}>
              {savePlaybook.isPending ? 'Сохраняем…' : pbDirty ? 'Сохранить правила' : 'Сохранено'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  on,
  onChange,
}: {
  label: string;
  hint?: string;
  /** true - функция работает. Пауза - это выключенный тумблер, а не включённая галочка. */
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-6 border-b border-line py-3.5 last:border-b-0">
      <div className="min-w-0">
        <div className="font-semibold">{label}</div>
        {hint ? <div className="mt-0.5 max-w-prose text-xs leading-snug text-muted">{hint}</div> : null}
      </div>
      {/* Никакой подписи «работает / на паузе»: состояние показывает сам тумблер.
          Дублировать его словом - лишний текст, который к тому же спорит с положением ручки. */}
      <Switch checked={on} onChange={onChange} aria-label={label} />
    </label>
  );
}

// Порядок сегментов - от самого тихого к самому самостоятельному: слева агент молчит,
// справа делает сам. Так переключатель читается как шкала доверия, а не как список опций.
const MODE_ORDER: AutonomyMode[] = ['off', 'confirm', 'auto_notify', 'auto'];

// Группируем по предметной области («Заезд и доступ», «Быт и техника») - так таблицу читает
// управляющий. Класс инструмента (чтение / отправка / необратимое) объясняет ДЕФОЛТ и остаётся
// подписью в строке: он важен, но это ответ на другой вопрос.
//
// Область берём из общего справочника @smena/core/labels, а НЕ из ответа сервера: поле area
// появилось позже, и панель, собранная новее API, обязана работать - иначе таблица молча
// схлопывается в пустоту (ровно это и случилось на стенде).
const toolArea = (name: string): AutonomyArea => TOOL_AREA[name as keyof typeof TOOL_AREA] ?? 'talk';
const intentArea = (name: string): AutonomyArea =>
  INTENT_AREA[name as keyof typeof INTENT_AREA] ?? 'talk';
const isHardStopIntent = (i: { name: string; hardStop?: boolean }): boolean =>
  i.hardStop ?? HARD_STOP_INTENTS.has(i.name as never);

function modeOptions(
  modeLabels: Record<AutonomyMode, string>,
  modeHints: Record<AutonomyMode, string>,
): Array<SegmentedOption<AutonomyMode>> {
  return MODE_ORDER.map((m) => ({
    value: m,
    label: modeLabels[m],
    title: modeHints[m],
  }));
}

// Названия/подсказки локализованы на сервере (AutonomyStateView.presetLabels / modeLabels / tools[].title).
// Здесь только маппинг режима → цвет чипа.

function AutonomyCard({ defaultMode }: { defaultMode: AutonomyMode }) {
  const qc = useQueryClient();
  const stateQ = useQuery({ queryKey: ['autonomy-state'], queryFn: fetchAutonomyState });

  const presetMut = useMutation({
    mutationFn: setAutonomyPreset,
    onSuccess: () => {
      toast.success('Уровень доверия изменён');
      void qc.invalidateQueries({ queryKey: ['autonomy-state'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const overrideMut = useMutation({
    mutationFn: (input: { key: string; mode: AutonomyMode; scope: 'tool' | 'intent' }) =>
      upsertAutonomyRule({ scope: input.scope, key: input.key, mode: input.mode }),
    onSuccess: () => {
      toast.success('Правило сохранено');
      void qc.invalidateQueries({ queryKey: ['autonomy-state'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const dropOverrideMut = useMutation({
    mutationFn: (input: { key: string; scope: 'tool' | 'intent' }) =>
      deleteAutonomyRule(input.scope, input.key),
    onSuccess: () => {
      toast.success('Правило сброшено');
      void qc.invalidateQueries({ queryKey: ['autonomy-state'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const state = stateQ.data;
  const preset = state?.preset ?? 'balance';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheckIcon weight="bold" className="size-4 text-accent-fg" /> Насколько Smena самостоятельна
        </CardTitle>
        <p className="max-w-prose text-xs leading-snug text-muted">
          Выберите общий уровень доверия - остальное подстроится само. Отдельное действие можно
          настроить точечно в таблице ниже. Деньги, жалобы и личные данные Smena не решает сама
          никогда, это правило выше любых настроек.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-2">
          {(state?.presets ?? (['strict', 'balance', 'free'] as AutonomyPreset[])).map((p) => {
            const label = state?.presetLabels?.[p] ?? p;
            const hint = state?.presetHints?.[p] ?? '';
            return (
              <button
                key={p}
                type="button"
                onClick={() => presetMut.mutate(p)}
                disabled={presetMut.isPending || p === preset}
                className={cn(
                  'rounded-md border p-3 text-left transition-colors',
                  p === preset
                    ? 'border-accent-fg bg-accent/15 text-accent-fg'
                    : 'border-line bg-panel2/40 hover:bg-panel2',
                )}
              >
                <div className="text-sm font-semibold">{label}</div>
                <div className="mt-1 text-[11px] text-muted">{hint}</div>
              </button>
            );
          })}
        </div>

        {state ? (
          <>
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-bg text-[11px] font-bold uppercase tracking-wide text-muted">
                    <th className="w-full min-w-56 px-4 py-2.5 text-left">Что делает</th>
                    <th className="px-4 py-2.5 text-left">Режим</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Группы - по классу инструмента: класс определяет дефолт из пресета,
                      поэтому он важнее как заголовок группы, чем как колонка в каждой строке. */}
                  {AREA_ORDER.map((area) => {
                    const rows = state.tools.filter((t) => toolArea(t.name) === area);
                    if (rows.length === 0) return null;
                    const free = rows.filter((t) => t.mode === 'auto' || t.mode === 'auto_notify').length;
                    return (
                      <Fragment key={area}>
                        <tr className="border-t border-line bg-bg">
                          <td colSpan={2} className="px-4 py-2">
                            <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
                              {AREA_LABEL_RU[area]}
                            </span>
                            <span className="float-right text-[11px] font-semibold text-muted">
                              {free} из {rows.length} делает сама
                            </span>
                          </td>
                        </tr>
                        {rows.map((t) => (
                          <ToolRow
                            key={t.name}
                            tool={t}
                            modeLabels={state.modeLabels}
                            modeHints={state.modeHints}
                            onOverride={(mode) => overrideMut.mutate({ key: t.name, mode, scope: 'tool' })}
                            onReset={() => dropOverrideMut.mutate({ key: t.name, scope: 'tool' })}
                            busy={overrideMut.isPending || dropOverrideMut.isPending}
                          />
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sprint 6-12: intent-scope override'ы. Показываем чувствительные интенты (жалобы, торг, инциденты). */}
            <div className="mt-6 border-t border-line pt-5">
              <div className="mb-1 flex items-center gap-2 font-semibold">
                <ChatCircleTextIcon weight="bold" className="size-4 text-accent-fg" />
                Вопросы гостей
              </div>
              <p className="mb-3 max-w-prose text-xs leading-snug text-muted">
                О чём вас спрашивают и как Smena на это отвечает. Своё правило на тему сильнее
                общей настройки: например «Жалоба» → «Спросит вас», и такой ответ отправите вы,
                даже если всё остальное идёт само.
              </p>
              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-bg text-[11px] font-bold uppercase tracking-wide text-muted">
                      <th className="w-full min-w-56 px-4 py-2.5 text-left">Тема вопроса</th>
                      <th className="px-4 py-2.5 text-left">Режим</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AREA_ORDER.map((area) => {
                      const rows = state.intents.filter((i) => intentArea(i.name) === area);
                      if (rows.length === 0) return null;
                      const auto = rows.filter((i) => !isHardStopIntent(i) && i.mode !== 'off').length;
                      return (
                        <Fragment key={area}>
                          <tr className="border-t border-line bg-bg">
                            <td colSpan={2} className="px-4 py-2">
                              <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
                                {AREA_LABEL_RU[area]}
                              </span>
                              <span className="float-right text-[11px] font-semibold text-muted">
                                {auto} из {rows.length} отвечает Smena
                              </span>
                            </td>
                          </tr>
                          {rows.map((i) => (
                            <IntentRow
                              key={i.name}
                              intent={i}
                              defaultMode={defaultMode}
                              modeLabels={state.modeLabels}
                              modeHints={state.modeHints}
                              onOverride={(mode) => overrideMut.mutate({ key: i.name, mode, scope: 'intent' })}
                              onReset={() => dropOverrideMut.mutate({ key: i.name, scope: 'intent' })}
                              busy={overrideMut.isPending || dropOverrideMut.isPending}
                            />
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-line bg-panel2/40 p-4 text-center text-sm text-muted">
            Загрузка…
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ToolRow({
  tool,
  modeLabels,
  modeHints,
  onOverride,
  onReset,
  busy,
}: {
  tool: AutonomyToolView;
  modeLabels: Record<AutonomyMode, string>;
  modeHints: Record<AutonomyMode, string>;
  onOverride: (mode: AutonomyMode) => void;
  onReset: () => void;
  busy: boolean;
}) {
  return (
    <tr className="border-t border-line align-top hover:bg-bg/60">
      <td className="px-4 py-3">
        {/* Технический идентификатор (get_availability и т.п.) владельцу не нужен - он живёт
            в подсказке и в журнале аудита. */}
        <div className="font-semibold" title={tool.name}>
          {tool.title}
        </div>
        <div className="mt-0.5 max-w-prose text-xs leading-snug text-muted">{tool.description}</div>
        <div className="mt-1 text-[11px] text-muted/80" title={tool.classHint}>
          {tool.classLabel}
        </div>
      </td>
      <td className="px-4 py-3">
        {/* Переключатель показывает и текущий режим, и все варианты. Отдельной колонки
            «откуда взялась настройка» нет: если режим отличается от общего, рядом появляется
            кнопка возврата - этого достаточно, а колонка была шумом в каждой строке. */}
        <div className="flex items-center gap-2">
          <Segmented
            aria-label={`Режим инструмента: ${tool.title}`}
            options={modeOptions(modeLabels, modeHints)}
            value={tool.mode}
            onChange={onOverride}
            disabled={busy}
          />
          {tool.reason === 'override' ? (
            <button
              type="button"
              onClick={onReset}
              disabled={busy}
              title="Вы меняли этот режим. Вернуть как у всех"
              aria-label="Вернуть режим как у всех"
              className="grid size-8 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-panel2 hover:text-text disabled:opacity-60"
            >
              <ArrowCounterClockwiseIcon weight="bold" className="size-4" />
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function IntentRow({
  intent,
  defaultMode,
  modeLabels,
  modeHints,
  onOverride,
  onReset,
  busy,
}: {
  intent: AutonomyIntentView;
  /** Что означает «Как у всех» прямо сейчас - зависит от «Отправки ответов гостю». */
  defaultMode: AutonomyMode;
  modeLabels: Record<AutonomyMode, string>;
  modeHints: Record<AutonomyMode, string>;
  onOverride: (mode: AutonomyMode) => void;
  onReset: () => void;
  busy: boolean;
}) {
  // «По умолчанию» - это отсутствие правила, а не пятый режим. В переключателе оно стоит
  // первым сегментом, потому что для оператора это такой же выбор, как и остальные;
  // на сервер уходит удаление правила, а не новый режим.
  const DEFAULT = 'default';
  // «По умолчанию» не отвечало на вопрос «а что именно из четырёх?». Теперь сегмент называется
  // «Как у всех», а колонка «Сейчас» показывает, во что это разворачивается на самом деле.
  const options: Array<SegmentedOption<AutonomyMode | typeof DEFAULT>> = [
    {
      value: DEFAULT,
      label: 'Как у всех',
      title: `Общая настройка: сейчас «${modeLabels[defaultMode]}»`,
    },
    ...modeOptions(modeLabels, modeHints),
  ];
  // Темы про деньги, жалобы и ущерб сервер всегда уводит человеку (ADR-6). Переключатель
  // для них заблокирован: выбор, который всё равно будет перебит политикой, - обман.
  const hardStop = intent.hardStop ?? HARD_STOP_INTENTS.has(intent.name as never);
  const blocker = intent.blocker ?? (hardStop ? 'Всегда человек' : null);

  return (
    <tr className="border-t border-line align-top hover:bg-bg/60">
      <td className="px-4 py-3">
        <div className="font-semibold" title={intent.name}>
          {intent.title}
        </div>
        <div className="mt-0.5 max-w-prose text-xs leading-snug text-muted">
          {intent.description}
          {blocker ? (
            <>
              {' '}
              <span className="font-semibold text-destructive">{blocker}</span> - это правило
              сервера, оно сильнее любых настроек.
            </>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3">
        <Segmented
          aria-label={`Правило для темы: ${intent.title}`}
          options={options}
          value={hardStop ? null : (intent.mode ?? DEFAULT)}
          onChange={(v) => (v === DEFAULT ? onReset() : onOverride(v))}
          disabled={busy || hardStop}
        />
      </td>
    </tr>
  );
}

/**
 * Ссылка на оплату. Smena подставляет в неё сумму сама и отправляет гостю только через слот -
 * выдумать ссылку или исказить сумму она не может (ADR-7, url-guard в резолвере).
 *
 * Показываем живое превью: без него владелец не поймёт, куда денется сумма, и будет гадать,
 * нужно ли дописывать параметр руками.
 */
function PaymentLinkField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const trimmed = value.trim();
  const preview = previewPaymentLink(trimmed);
  const invalid = trimmed.length > 0 && preview === null;

  return (
    <div className="flex flex-col gap-1.5">
      <Label>Ссылка на оплату</Label>
      <Input
        type="url"
        inputMode="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://pay.example.ru/order"
        className={cn(invalid && 'border-destructive')}
        aria-invalid={invalid}
      />
      {invalid ? (
        <span className="text-xs text-destructive">
          Не похоже на ссылку. Нужен адрес целиком, начиная с https://
        </span>
      ) : preview ? (
        <span className="text-xs text-muted">
          Гость получит:{' '}
          <span className="font-semibold text-text">{preview}</span> - сумму Smena подставит сама
        </span>
      ) : (
        <span className="max-w-prose text-xs leading-snug text-muted">
          Ссылку выдаёт ваш банк или платёжный сервис. Сумму Smena подставит сама: если в ссылке
          есть «{'{amount}'}» - туда, иначе добавит «?amount=». Пока поле пустое, Smena не
          отправляет ссылки на оплату и зовёт вас.
        </span>
      )}
    </div>
  );
}

/**
 * Тон общения. Раньше это было свободное поле, куда владелец должен был сам сочинить
 * инструкцию для модели - непонятно, что писать и чем один вариант отличается от другого.
 * Теперь три готовых тона, а поле остаётся для тех, кому нужна своя формулировка.
 */
const TONE_PRESETS: Array<{ id: string; label: string; text: string }> = [
  {
    id: 'friendly',
    label: 'Дружелюбно',
    text: 'дружелюбно, на «вы», коротко и по делу, без канцелярита; максимум один эмодзи',
  },
  {
    id: 'neutral',
    label: 'Нейтрально',
    text: 'нейтрально, на «вы», коротко и по делу, без эмодзи',
  },
  {
    id: 'formal',
    label: 'Официально',
    text: 'официально, на «вы», полными предложениями, без сокращений и эмодзи',
  },
];

function ToneField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const preset = TONE_PRESETS.find((t) => t.text === value.trim());
  // «Своя формулировка» - режим редактирования, а не значение: по одному только тексту
  // нельзя отличить «выбрал готовый тон» от «написал ровно те же слова руками».
  // Инициализировать состояние значением нельзя - плейбук приезжает асинхронно, и на первом
  // рендере тон ещё пустой; поэтому храним только явный выбор пользователя.
  const [customChosen, setCustom] = useState(false);
  const selected = customChosen || !preset ? 'custom' : preset.id;
  const example =
    selected === 'friendly'
      ? 'Конечно! Заезд с 15:00, код от замка пришлю за 3 часа 🙂'
      : selected === 'neutral'
        ? 'Заезд с 15:00. Код от замка пришлю за 3 часа до заезда.'
        : 'Добрый день. Заезд возможен с 15:00. Код доступа будет направлен за три часа до заезда.';

  return (
    <div className="flex flex-col gap-2">
      <Label>Тон общения с гостем</Label>
      <Segmented
        aria-label="Тон общения с гостем"
        value={selected}
        onChange={(id) => {
          if (id === 'custom') {
            setCustom(true);
            return;
          }
          const next = TONE_PRESETS.find((t) => t.id === id);
          if (next) {
            setCustom(false);
            onChange(next.text);
          }
        }}
        options={[
          ...TONE_PRESETS.map((t) => ({ value: t.id, label: t.label })),
          { value: 'custom', label: 'Своя формулировка' },
        ]}
      />
      {selected === 'custom' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          placeholder="Опишите тон своими словами: как Smena должна звучать"
          className="w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
        />
      ) : (
        <div className="rounded-md bg-panel2 px-3 py-2 text-sm text-muted">
          Гость увидит примерно так: «{example}»
        </div>
      )}
    </div>
  );
}

function PlaybookField({
  label,
  k,
  v,
  onChange,
  type,
  hint,
}: {
  label: string;
  k: string;
  v: string | number | undefined;
  onChange: (k: string, v: unknown) => void;
  type?: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input
        type={type ?? 'text'}
        value={v ?? ''}
        onChange={(e) => onChange(k, type === 'number' ? Number(e.target.value) : e.target.value)}
        placeholder={hint}
      />
    </div>
  );
}
